import * as THREE from 'three';
import { useAppStore, CameraKeyframe, Layer, LayerKeyframes } from '../store/useAppStore';

export interface LayerHistoryEntry {
  layers: Layer[];
  selectedLayerId: string | null;
  layerKeyframes: Record<string, LayerKeyframes>;
  cameraKeyframes: CameraKeyframe;
  /** GPU render targets holding each layer's pixel snapshot. Dispose when evicted or consumed. */
  snapshotRTs: Map<string, THREE.WebGLRenderTarget>;
}

/**
 * Dispose all GPU snapshot render targets in an entry.
 * Call when the entry is evicted from the undo/redo stack or after it has been restored.
 */
export function disposeLayerHistoryEntry(entry: LayerHistoryEntry): void {
  entry.snapshotRTs.forEach((rt) => rt.dispose());
  entry.snapshotRTs.clear();
}

type LayerRTMap = Map<string, { a: THREE.WebGLRenderTarget; b: THREE.WebGLRenderTarget; active: 'a' | 'b' }>;

export interface LayerHistoryRefs {
  glRef: React.RefObject<any>;
  copyMaterial: React.RefObject<THREE.ShaderMaterial | null>;
  copyScene: React.RefObject<THREE.Scene | null>;
  orthoCamera: React.RefObject<THREE.OrthographicCamera | null>;
  layerRTs: React.MutableRefObject<LayerRTMap>;
  ensureLayerRTsRef: React.MutableRefObject<(layers: Layer[]) => void>;
  flattenLayersRef: React.MutableRefObject<() => void>;
  scheduleBumpRef: React.MutableRefObject<(after?: () => void) => void>;
  generateAllLayerThumbnailsRef: React.MutableRefObject<() => void>;
}

export interface LayerHistoryActions {
  captureLayerSnapshot: () => LayerHistoryEntry;
  restoreLayerEntry: (entry: LayerHistoryEntry) => Promise<void>;
  /** True while a restore is in progress — callers should guard against concurrent ops. */
  isLayerOpInProgress: { current: boolean };
}

/**
 * Captures and restores full world state (layers, keyframes, pixel data).
 * Snapshot capture uses fast GPU→GPU render target blits instead of CPU readbacks,
 * eliminating the per-action stall that was caused by readRenderTargetPixels + WebP encoding.
 */
export function useLayerHistory(refs: LayerHistoryRefs): LayerHistoryActions {
  const {
    glRef,
    copyMaterial,
    copyScene,
    orthoCamera,
    layerRTs,
    ensureLayerRTsRef,
    flattenLayersRef,
    scheduleBumpRef,
    generateAllLayerThumbnailsRef,
  } = refs;

  // Prevents concurrent undo/redo operations from racing each other.
  const isLayerOpInProgress = { current: false };

  const captureLayerSnapshot = (): LayerHistoryEntry => {
    const store = useAppStore.getState();
    const layers = store.layers.map((l) => ({
      ...l, translate: { ...l.translate }, rotate: { ...l.rotate },
    }));
    const layerKeyframes: Record<string, LayerKeyframes> = {};
    for (const [lid, kfs] of Object.entries(store.layerKeyframes)) {
      layerKeyframes[lid] = {} as LayerKeyframes;
      for (const p of Object.keys(kfs) as Array<keyof LayerKeyframes>) {
        (layerKeyframes[lid] as any)[p] = { ...(kfs[p] as any) };
      }
    }
    const cameraKeyframes: CameraKeyframe = {};
    for (const [f, v] of Object.entries(store.cameraKeyframes)) {
      cameraKeyframes[Number(f)] = { ...v };
    }

    // GPU→GPU blit: copy each layer's active RT into a dedicated snapshot RT.
    // This takes <1 ms (no CPU readback, no encoding) and eliminates the
    // first-frame stall that previously occurred on every pointer-down.
    const snapshotRTs = new Map<string, THREE.WebGLRenderTarget>();
    const gl = glRef.current;
    const cm = copyMaterial.current;
    const cs = copyScene.current;
    const oc = orthoCamera.current;
    if (gl && cm && cs && oc) {
      for (const layer of store.layers) {
        const lRTData = layerRTs.current.get(layer.id);
        if (!lRTData) continue;
        const activeRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
        const snap = new THREE.WebGLRenderTarget(activeRT.width, activeRT.height, {
          type: THREE.UnsignedByteType,
          minFilter: THREE.LinearFilter,
          magFilter: THREE.LinearFilter,
        });
        snap.texture.colorSpace = THREE.SRGBColorSpace;
        cm.uniforms.uTex.value = activeRT.texture;
        gl.setRenderTarget(snap);
        gl.render(cs, oc);
        gl.setRenderTarget(null);
        snapshotRTs.set(layer.id, snap);
      }
    }

    return { layers, selectedLayerId: store.selectedLayerId, layerKeyframes, cameraKeyframes, snapshotRTs };
  };

  // Restore is now synchronous (GPU→GPU blits), wrapped in a Promise for API compatibility.
  const restoreLayerEntry = async (entry: LayerHistoryEntry): Promise<void> => {
    useAppStore.getState().restoreLayerState(
      entry.layers, entry.selectedLayerId, entry.layerKeyframes, entry.cameraKeyframes,
    );
    ensureLayerRTsRef.current(entry.layers);
    const restoredIds = new Set(entry.layers.map((l) => l.id));
    layerRTs.current.forEach((lRT, id) => {
      if (!restoredIds.has(id)) {
        lRT.a.dispose();
        lRT.b.dispose();
        layerRTs.current.delete(id);
      }
    });
    const gl = glRef.current;
    const cm = copyMaterial.current;
    const cs = copyScene.current;
    const oc = orthoCamera.current;
    if (gl && cm && cs && oc) {
      for (const layer of entry.layers) {
        const snapRT = entry.snapshotRTs.get(layer.id);
        const lRTData = layerRTs.current.get(layer.id);
        if (!snapRT || !lRTData) continue;
        // Blit snapshot back into the layer's active RT.
        cm.uniforms.uTex.value = snapRT.texture;
        gl.setRenderTarget(lRTData.a);
        gl.render(cs, oc);
        gl.setRenderTarget(null);
        lRTData.active = 'a';
      }
    }
    flattenLayersRef.current();
    scheduleBumpRef.current();
    generateAllLayerThumbnailsRef.current();
    // Note: caller is responsible for calling disposeLayerHistoryEntry(entry)
    // after this resolves, to free the snapshot RTs.
  };

  return { captureLayerSnapshot, restoreLayerEntry, isLayerOpInProgress };
}
